import { Badge } from "primereact/badge";
import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import { useEffect } from "react";
import ApplicationCertificatesTab from "./ApplicationCertificatesTab";
import PasswordUsersTab from "./PasswordUsersTab";
import X509UsersTab from "./X509UsersTab";
import { useOpcUaCertificates } from "./hooks/useOpcUaCertificates";
import { useOpcUaUsers } from "./hooks/useOpcUaUsers";

interface OpcUaManagementProps {
  close(): void;
}

/**
 * Utilities → OPC UA Management.
 *
 * Both hooks live here rather than in the individual tabs so that a single certificate
 * fetch feeds the X.509 and application-certificate tabs, and so the data survives tab
 * switching.
 */
const OpcUaManagement = ({ close }: OpcUaManagementProps) => {
  const {
    users,
    isLoading: isUsersLoading,
    fetchUsers,
    saveUser,
    changePassword,
    setUserEnabled,
    deleteUser,
  } = useOpcUaUsers();

  const {
    x509Users,
    applicationCertificates,
    pendingCount,
    isLoading: isCertificatesLoading,
    isBusy,
    fetchCertificates,
    trustCertificate,
    unbanCertificate,
    revokeCertificate,
    deleteCertificate,
    downloadCertificate,
    uploadTrustedCertificate,
    createX509User,
    importX509User,
    bulkTrust,
    bulkRevoke,
    bulkDelete,
  } = useOpcUaCertificates();

  useEffect(() => {
    fetchUsers();
    fetchCertificates();
  }, [fetchUsers, fetchCertificates]);

  const certificatesTabHeader = (
    <span className="flex align-items-center gap-2">
      Application Certificates
      {pendingCount > 0 && <Badge value={pendingCount} severity="warning" />}
    </span>
  );

  return (
    <Dialog
      visible
      onHide={close}
      header="OPC UA Management"
      style={{ width: "min(1100px, 95vw)", height: "85vh" }}
      breakpoints={{ "960px": "95vw" }}
      contentStyle={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/*
        confirmDialog() calls use the global <ConfirmDialog /> mounted in AppHeader.
        panelContainer overflow is hidden rather than auto: each tab's DataTable owns its
        own scrolling via scrollHeight="flex", so a second scroll container would nest
        scrollbars.
      */}
      <TabView
        className="flex flex-column"
        style={{ flex: 1, minHeight: 0 }}
        panelContainerStyle={{ flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        <TabPanel header="Password Users">
          <PasswordUsersTab
            users={users}
            isLoading={isUsersLoading}
            fetchUsers={fetchUsers}
            saveUser={saveUser}
            changePassword={changePassword}
            setUserEnabled={setUserEnabled}
            deleteUser={deleteUser}
          />
        </TabPanel>
        <TabPanel header="X.509 Users">
          <X509UsersTab
            x509Users={x509Users}
            isLoading={isCertificatesLoading}
            isBusy={isBusy}
            fetchCertificates={fetchCertificates}
            createX509User={createX509User}
            importX509User={importX509User}
            deleteCertificate={deleteCertificate}
            downloadCertificate={downloadCertificate}
          />
        </TabPanel>
        <TabPanel header={certificatesTabHeader}>
          <ApplicationCertificatesTab
            applicationCertificates={applicationCertificates}
            pendingCount={pendingCount}
            isLoading={isCertificatesLoading}
            isBusy={isBusy}
            fetchCertificates={fetchCertificates}
            trustCertificate={trustCertificate}
            unbanCertificate={unbanCertificate}
            revokeCertificate={revokeCertificate}
            deleteCertificate={deleteCertificate}
            downloadCertificate={downloadCertificate}
            uploadTrustedCertificate={uploadTrustedCertificate}
            bulkTrust={bulkTrust}
            bulkRevoke={bulkRevoke}
            bulkDelete={bulkDelete}
          />
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default OpcUaManagement;
